// Cross-device player account API client.
//
// Each player gets a 4-digit code on first "Create player". The code is the
// only credential — entering it on another device pulls down the full saved
// profile. After every progress change we fire-and-forget sync the profile
// blob back to the server so it stays current.
import type { LocalProfile } from "./profile";

export interface CreateResult {
  code: string;
}

export async function createPlayerOnServer(profile: LocalProfile): Promise<CreateResult> {
  const res = await fetch("/api/player/create", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ profile }),
  });
  if (!res.ok) throw new Error(`create failed: ${res.status}`);
  return (await res.json()) as CreateResult;
}

export async function loadPlayerFromServer(code: string): Promise<LocalProfile | null> {
  const res = await fetch("/api/player/load", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ code }),
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`load failed: ${res.status}`);
  const body = (await res.json()) as { profile: LocalProfile };
  return body.profile;
}

let _syncTimer = 0;
let _pending: LocalProfile | null = null;

/** Debounced sync. Stacks rapid edits and pushes the latest one after 800ms. */
export function syncPlayerToServer(profile: LocalProfile) {
  if (!profile.code) return;
  _pending = profile;
  if (_syncTimer) return;
  _syncTimer = window.setTimeout(() => {
    _syncTimer = 0;
    const p = _pending;
    _pending = null;
    if (!p || !p.code) return;
    void fetch("/api/player/sync", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ code: p.code, profile: p }),
    }).catch(() => {
      // Silent failure — local progress is still safe; next sync retries.
    });
  }, 800);
}
